import { URLSearchParamsInit, useSearchParams } from 'react-router-dom';

type QueryStringType = string | string[] | null;

function useSearchParamsState<T extends QueryStringType>(
  searchParamName: string,
  defaultValue: QueryStringType,
  isList?: boolean
): readonly [searchParamsState: T, setSearchParamsState: (newState: T) => void];
function useSearchParamsState(
  searchParamName: string,
  defaultValue: QueryStringType,
  isList?: false
): readonly [searchParamsState: string, setSearchParamsState: (newState: QueryStringType) => void];
function useSearchParamsState(
  searchParamName: string,
  defaultValue: QueryStringType,
  isList: true
): readonly [
  searchParamsState: string[],
  setSearchParamsState: (newState: QueryStringType) => void,
];
function useSearchParamsState(
  searchParamName: string,
  defaultValue: QueryStringType,
  isList: boolean = false
): readonly [
  searchParamsState: QueryStringType,
  setSearchParamsState: (newState: QueryStringType) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();

  let acquiredSearchParam;
  if (isList) {
    acquiredSearchParam = searchParams.getAll(searchParamName);
  } else {
    acquiredSearchParam = searchParams.get(searchParamName);
  }

  const searchParamsState =
    !acquiredSearchParam || acquiredSearchParam.length === 0 ? defaultValue : acquiredSearchParam;

  const setSearchParamsState = (newState: QueryStringType) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setSearchParams((_prevSearchParams) => {
      // react-router-dom bug: https://github.com/remix-run/react-router/issues/9757
      // Successive calls to setSearchParams() with an updater function argument will receive the same stale data
      const prevSearchParams = new URLSearchParams(window.location.search);
      return {
        ...[...prevSearchParams.entries()].reduce(
          (o, [key, value]): { [key: string]: QueryStringType } => {
            if (o[key] === undefined) {
              return { ...o, [key]: value };
            } else if (typeof o[key] === 'string') {
              return { ...o, [key]: [o[key], value] };
            } else {
              return { ...o, [key]: [...o[key], value] };
            }
          },
          {}
        ),
        [searchParamName]: newState,
      } as URLSearchParamsInit;
    });
  };
  return [searchParamsState, setSearchParamsState];
}

export { useSearchParamsState };
